<template>
  <div class="cash-in-out-v2" data-cy="cash-transfers-modal-v2">
    <SDialog :open="dialogFormVisible" @update:open="onOpenChange">
      <!-- L3-8419: 560 rather than 500 so the German footer labels fit on one
           line. See the footer comment below. -->
      <SDialogContent
        :show-close-button="true"
        class="cash-in-out-v2 !w-[560px] !max-w-[560px]"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="flex items-center gap-2">
            <CircleCheckIcon
              v-if="step === 'success'"
              class="w-5 h-5 text-emerald-500 bg-emerald-50 rounded-full"
            />
            <ArrowLeftRightIcon v-else class="w-5 h-5 text-[#044c55]" />
            {{
              step === "success"
                ? t("cashtransfer.v2.title-success")
                : t("cashtransfer.v2.title")
            }}
          </SDialogTitle>
        </SDialogHeader>

        <!-- Transfer in / out tabs — switchable in the form, locked (read-only)
             in the review step so the chosen direction stays visible. -->
        <div
          v-if="step === 'form' || step === 'review'"
          class="flex items-center border-t-2 border-b-2 border-slate-200"
        >
          <SButton
            type="button"
            variant="ghost"
            class="flex-1 flex items-center justify-center gap-2 py-2.5 p-2 text-sm font-medium transition disabled:cursor-not-allowed rounded-none"
            :class="
              transferType === 'in'
                ? 'text-[#044c55] bg-[#044c55]/10 border-b-2 border-[#044c55]'
                : 'text-muted-foreground hover:bg-slate-100'
            "
            :disabled="step === 'review'"
            data-cy="v2-transfer-in-tab"
            @click="transferType = 'in'"
          >
            <ArrowDownIcon class="w-4 h-4" />
            {{ t("cashtransfer.v2.transfer-in") }}
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            class="flex-1 flex items-center justify-center gap-2 py-2.5 p-2 text-sm font-medium transition disabled:cursor-not-allowed rounded-none"
            :class="
              transferType === 'out'
                ? 'text-[#044c55] bg-[#044c55]/10 border-b-2 border-[#044c55]'
                : 'text-muted-foreground hover:bg-slate-100'
            "
            :disabled="step === 'review'"
            data-cy="v2-transfer-out-tab"
            @click="transferType = 'out'"
          >
            <ArrowUpIcon class="w-4 h-4" />
            {{ t("cashtransfer.v2.transfer-out") }}
          </SButton>
        </div>

        <div v-if="step === 'form'" class="flex flex-col gap-4 py-2">
          <!-- Source location -->
          <div>
            <label class="text-sm font-medium block mb-1">
              {{
                transferType === "in"
                  ? t("cashtransfer.v2.source-location")
                  : t("cashtransfer.v2.destination-location")
              }}
              <span class="text-red-500">*</span>
            </label>
            <SSelect v-model="locationId" data-cy="v2-location-select">
              <SSelectTrigger class="w-full">
                <component
                  v-if="selectedLocationOption"
                  :is="selectedLocationOption.icon"
                  class="w-4 h-4 mr-2"
                  :class="selectedLocationOption.colorClass"
                />
                <SSelectValue
                  :placeholder="
                    transferType === 'in'
                      ? t('cashtransfer.v2.select-source')
                      : t('cashtransfer.v2.select-destination')
                  "
                />
              </SSelectTrigger>
              <SSelectContent>
                <SSelectItem
                  v-for="opt in locationOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  <span class="flex items-center gap-2">
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                      :class="opt.bgClass"
                    >
                      <component
                        :is="opt.icon"
                        class="w-3.5 h-3.5"
                        :class="opt.colorClass"
                      />
                    </span>
                    <span>{{ opt.label }}</span>
                  </span>
                </SSelectItem>
              </SSelectContent>
            </SSelect>
            <p class="text-xs text-muted-foreground mt-1">
              {{ t("cashtransfer.v2.kiosk-excluded") }}
            </p>
          </div>

          <!-- Currency pills -->
          <div>
            <label class="text-sm font-medium block mb-2">
              {{ t("cashtransfer.v2.currency") }}
              <span class="text-red-500">*</span>
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="c in availableCurrencies"
                :key="c.label"
                type="button"
                class="relative px-4 py-1.5 rounded-md border text-sm transition shadow-sm"
                :class="
                  currency === c.label
                    ? 'text-white bg-[#044c55] border-[#044c55] shadow-md'
                    : 'bg-white text-[#044c55] border-slate-200 '
                "
                :data-cy="`v2-currency-${c.label}`"
                @click="selectCurrency(c.label)"
              >
                {{ c.label }}
                <span
                  v-if="hasAmountFor(c.label)"
                  class="absolute -top-1 -right-1 inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white currency-dot"
                  :data-cy="`v2-currency-dot-${c.label}`"
                ></span>
              </button>
            </div>
          </div>

          <!-- Payment method -->
          <div>
            <label class="text-sm font-medium block mb-1">
              {{ t("cashtransfer.v2.payment-method") }}
              <span class="text-red-500">*</span>
            </label>
            <SSelect v-model="paymentMethod" data-cy="v2-payment-method">
              <SSelectTrigger class="w-full">
                <component
                  v-if="selectedPaymentMethodOption"
                  :is="selectedPaymentMethodOption.icon"
                  class="w-4 h-4 mr-2"
                  :class="selectedPaymentMethodOption.colorClass"
                />
                <SSelectValue
                  :placeholder="t('cashtransfer.v2.select-method')"
                />
              </SSelectTrigger>
              <SSelectContent>
                <SSelectItem
                  v-for="m in paymentMethodOptions"
                  :key="m.value"
                  :value="m.value"
                >
                  <span class="flex items-center gap-2">
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                      :class="m.bgClass"
                    >
                      <component
                        :is="m.icon"
                        class="w-3.5 h-3.5"
                        :class="m.colorClass"
                      />
                    </span>
                    <span>{{ m.label }}</span>
                  </span>
                </SSelectItem>
              </SSelectContent>
            </SSelect>
          </div>

          <!-- Amount (for the selected currency; multi-currency supported) -->
          <div>
            <label class="text-sm font-medium block mb-1">
              {{
                t("cashtransfer.v2.amount", {
                  currency: currency,
                })
              }}
              <span class="text-red-500">*</span>
            </label>
            <SInputGroup>
              <SInputGroupInput
                v-model="amounts[currency]"
                type="number"
                :min="0"
                placeholder="0.00"
                :data-cy="`v2-amount-input-${currency}`"
              />
              <SInputGroupAddon align="inline-end">
                <SInputGroupText class="font-semibold">{{
                  currency
                }}</SInputGroupText>
              </SInputGroupAddon>
            </SInputGroup>
            <p
              v-if="multiCurrencyHint"
              class="text-xs text-muted-foreground mt-1"
            >
              {{ t("cashtransfer.v2.multi-currency-hint") }}
            </p>
            <!-- Current drawer balance for the selected currency (cash-out only). -->
            <p
              v-if="transferType === 'out'"
              class="text-xs text-muted-foreground mt-1"
              data-cy="v2-current-drawer"
            >
              {{ t("cashtransfer.v2.current-drawer", { currency }) }}
              <span class="text-[#044c55] font-medium">
                {{ currency }}
                {{ formatDrawer(currentDrawerAmount) }}
              </span>
            </p>
          </div>

          <!-- Notes (optional) -->
          <div>
            <label class="text-sm font-medium block mb-1">
              {{ t("cashtransfer.v2.notes") }}
            </label>
            <STextarea
              v-model="note"
              :rows="2"
              :placeholder="t('cashfloat.v2.add-note-placeholder')"
              data-cy="v2-note-input"
            />
          </div>
          <div
            v-if="exceedsDrawer"
            class="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-3 py-2 text-sm"
            data-cy="v2-transfer-exceeds-drawer"
          >
            <TriangleAlertIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <p>{{ t("cashtransfer.v2.exceeds-drawer") }}</p>
          </div>

          <!-- Offline notice — transfers need an active connection. -->
          <div
            v-if="!isOnline"
            class="flex items-start gap-2 bg-slate-800 text-white rounded-md px-3 py-2 text-sm"
            data-cy="v2-transfer-offline-notice"
          >
            <InfoIcon class="w-4 h-4 mt-0.5 shrink-0" />
            <p>{{ t("cashtransfer.v2.offline-tooltip") }}</p>
          </div>
        </div>

        <!-- Review / Success step -->
        <div
          v-if="step === 'review' || step === 'success'"
          class="flex flex-col gap-4 py-4"
          :data-cy="
            step === 'success'
              ? 'v2-cash-transfer-success'
              : 'v2-cash-transfer-review'
          "
        >
          <div class="flex flex-col items-center gap-2">
            <span
              class="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#044c55] bg-[#044c55]/5 text-[#044c55] shadow-sm"
              data-cy="v2-review-source"
            >
              <component
                v-if="reviewTopParty"
                :is="reviewTopParty.icon"
                class="w-4 h-4 text-[#044c55]"
              />
              <span class="font-medium">
                {{ reviewTopParty?.label }}
              </span>
            </span>
            <span class="relative inline-flex items-center justify-center">
              <component :is="reviewArrowIcon" class="w-4 h-4 text-[#044c55]" />
            </span>
            <span
              class="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#044c55] bg-[#044c55]/5 text-[#044c55] shadow-sm"
              data-cy="v2-review-destination"
            >
              <component
                v-if="reviewBottomParty"
                :is="reviewBottomParty.icon"
                class="w-4 h-4 text-[#044c55]"
              />
              <span class="font-medium">
                {{ reviewBottomParty?.label }}
              </span>
            </span>
          </div>

          <div
            class="rounded-md bg-slate-50 border border-slate-200 divide-y divide-slate-200 text-sm"
          >
            <div class="flex items-center justify-between px-4 py-2">
              <span class="text-muted-foreground">
                {{ t("cashtransfer.v2.review-transfer-type") }}
              </span>
              <span class="font-semibold">
                {{
                  transferType === "in"
                    ? t("cashtransfer.v2.review-cash-in")
                    : t("cashtransfer.v2.review-cash-out")
                }}
              </span>
            </div>
            <div class="flex items-center justify-between px-4 py-2">
              <span class="text-muted-foreground">
                {{ t("cashtransfer.v2.payment-method") }}
              </span>
              <span class="font-semibold">
                {{ selectedPaymentMethodLabel }}
              </span>
            </div>
            <div
              v-for="entry in nonZeroEntries"
              :key="entry.currency"
              class="flex items-center justify-between px-4 py-2.5 bg-[#044c55]/8"
            >
              <span class="font-medium text-[#044c55]">
                {{
                  t("cashtransfer.v2.amount", {
                    currency: entry.currency,
                  })
                }}
              </span>
              <span class="text-base font-bold text-[#044c55]">
                {{ entry.currency }}
                {{ formatMoney(entry.amount) }}
              </span>
            </div>
            <div class="flex items-center justify-between px-4 py-2">
              <span class="text-muted-foreground">
                {{ t("cashtransfer.v2.notes") }}
              </span>
              <span class="font-medium text-right max-w-[60%] truncate">
                {{ note.trim() || "—" }}
              </span>
            </div>
          </div>

          <div
            v-if="step === 'review'"
            class="flex items-start gap-2 bg-sky-50 border border-sky-200 text-sky-800 rounded-md px-3 py-2 text-sm"
          >
            <TriangleAlertIcon class="w-4 h-4 mt-0.5 shrink-0 text-sky-500" />
            <p>{{ t("cashtransfer.v2.review-warning") }}</p>
          </div>
        </div>

        <!-- L3-8419: the footer is one non-wrapping row, and at the old 500px
             the German labels ("Abbrechen & zurückkehren" plus "Überprüfen und
             bestätigen") did not fit. SDialogContent is a CSS grid, so the
             oversized row widened the column and pushed the content past the
             modal's edge. The dialog is wider now so the pair stays on one
             line; min-w-0 keeps the footer from growing the column again if a
             longer translation ever appears. -->
        <SDialogFooter class="gap-3 min-w-0">
          <template v-if="step === 'form'">
            <SButton
              type="button"
              variant="outline"
              data-cy="v2-cash-transfer-cancel"
              @click="closeModal"
            >
              <CircleXIcon class="w-4 h-4 mr-2" />
              {{ t("cashtransfer.v2.cancel-and-return") }}
            </SButton>
            <SButton
              type="button"
              :disabled="!canConfirm || !isOnline"
              :title="!isOnline ? t('cashtransfer.v2.offline-tooltip') : ''"
              data-cy="v2-cash-transfer-review"
              @click="step = 'review'"
            >
              <CheckIcon class="w-4 h-4 mr-2" />
              {{ t("cashtransfer.v2.review-and-confirm") }}
            </SButton>
          </template>
          <template v-else-if="step === 'review'">
            <SButton
              type="button"
              variant="outline"
              data-cy="v2-cash-transfer-back"
              :disabled="isLoading"
              @click="step = 'form'"
            >
              <ArrowLeftIcon class="w-4 h-4 mr-2" />
              {{ $t("button.back") }}
            </SButton>
            <SButton
              type="button"
              :loading="isLoading"
              :disabled="!isOnline"
              :title="!isOnline ? t('cashtransfer.v2.offline-tooltip') : ''"
              data-cy="v2-cash-transfer-confirm"
              @click="submit"
            >
              <CheckIcon class="w-4 h-4 mr-2" />
              {{ t("cashtransfer.v2.confirm-transfer") }}
            </SButton>
          </template>
          <template v-else>
            <SButton
              type="button"
              variant="outline"
              class="flex-1"
              data-cy="v2-cash-transfer-close"
              @click="closeModal"
            >
              <CircleXIcon class="w-4 h-4 mr-2" />
              {{ $t("button.close") }}
            </SButton>
            <SButton
              type="button"
              class="flex-1"
              data-cy="v2-cash-transfer-new"
              @click="startNewTransfer"
            >
              <RefreshCwIcon class="w-4 h-4 mr-2" />
              {{ t("cashtransfer.v2.new-transfer") }}
            </SButton>
          </template>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import randomstring from "randomstring";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SInputGroupText,
  STextarea,
} from "@smeetz/ui-core";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowLeftRightIcon,
  ArrowUpIcon,
  BanknoteIcon,
  CheckIcon,
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  MonitorIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "@smeetz/ui-icons";
import { markRaw } from "vue";
import { MoneyInOutType } from "@smeetz/compliance-common";
import { PaymentMethod } from "@smeetz/order-common";

import { ModalStatus } from "@/constants/modal";
import { Currencies } from "@/constants/currency";
import { PaymentMethods } from "@/types/db/accounting";
import { CashLocationDeviceType } from "@/constants/cash-location";
import ModalModule from "@/store/modules/modal";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import CashFloatModule from "@/store/modules/cash-float";
import type { ICashTransfer } from "@/interfaces/cash-float/cash-float";
import { signCashTransferTransaction } from "@/utils/helpers/fiskaly";
import { handleCashTransferSync } from "@/utils/vue-helpers/cash-float";
import { locationVisualFor } from "@/utils/vue-helpers/cash-location";
import {
  mapCashLocation,
  processMoneyInOutEvent,
} from "@/utils/helpers/compliance";
import { getManualClosingSummaryV2 as getManualClosingSummary } from "@/api/cash-float/cash-float";
import NotificationService from "@/services/notification.service";
import { LogService } from "@/services/logging";

const { t } = useI18n({});

// ---------- Connectivity ----------
// Cash transfers sync to the backend, so they require an active connection.
// Track online/offline reactively to disable confirmation while offline.
const isOnline = ref(
  typeof navigator !== "undefined" ? navigator.onLine : true
);
function handleOnline() {
  isOnline.value = true;
}
function handleOffline() {
  isOnline.value = false;
}
// Live per-currency drawer amounts fetched from the closing summary API.
// Populated on mount so the balance reflects all previous transfers immediately.
const liveAmounts = ref<Record<string, number>>({});

onMounted(async () => {
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  DeviceModule.fetchLocations();

  const uid = CashFloatModule.cashFloat?.uid;
  if (uid) {
    try {
      const summary = await getManualClosingSummary(uid);
      const cashMethod = (summary?.amountsPerPaymentMethods || []).find(
        (m: any) => m.name === PaymentMethods.METHOD_CASH
      );
      if (cashMethod?.amounts) {
        liveAmounts.value = cashMethod.amounts;
      }
    } catch {
      // fall back to cashFloat.amounts silently
    }
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
});

const dialogFormVisible = ref(true);
const isLoading = ref(false);
const step = ref<"form" | "review" | "success">("form");
const transferType = ref<"in" | "out">("in");
const locationId = ref<number | null>(null);
// Amounts entered per currency for multi-currency transfers (one tx per non-zero entry).
const amounts = ref<Record<string, string>>({});
const note = ref<string>("");
// Empty by default so the selector shows the "select method" placeholder first;
// the cashier must explicitly pick Cash before confirming.
const paymentMethod = ref<string>("");

// ---------- Currency ----------
// Mirrors NewCashFloatOpenV2's fallback: when the group only has one currency
// enabled, expose every supported currency so the cashier can still pick.
type CurrencyOption = {
  id: number | string;
  isDefault: number;
  status: number;
  label: string;
  exchangeRate: number;
};
// Countries where multi-currency cash transfers are not allowed.
const MULTI_CURRENCY_BLOCKED_COUNTRIES = new Set([
  "BE",
  "FR",
  "DE",
  "AT",
  "ES",
  "IT",
]);
const isMultiCurrencyBlocked = computed(() =>
  MULTI_CURRENCY_BLOCKED_COUNTRIES.has(POSConfigModule.getGroupCountryCode)
);

const availableCurrencies = computed<CurrencyOption[]>(() => {
  const enabled = (POSConfigModule.groupCurrencies as CurrencyOption[]) || [];
  // In blocked countries, only expose the default (main) currency.
  const defaultOnly = enabled.filter((c) => c.isDefault === 1);
  if (isMultiCurrencyBlocked.value)
    return defaultOnly.length ? defaultOnly : enabled.slice(0, 1);
  if (enabled.length > 1) return enabled;
  const defaultLabel = enabled[0]?.label;
  return Object.values(Currencies).map((label, i) => ({
    id: i,
    isDefault: label === defaultLabel ? 1 : 0,
    status: 1,
    label: String(label),
    exchangeRate: 1,
  }));
});

const defaultCurrency = computed<string>(() => {
  return (
    availableCurrencies.value.find((c) => c.isDefault === 1)?.label ||
    availableCurrencies.value[0]?.label ||
    Currencies.CHF
  );
});

const currency = ref<string>(defaultCurrency.value);

function selectCurrency(label: string) {
  currency.value = label;
}

// ---------- Payment method ----------
// Cash transfers move physical cash only — card/check have no drawer impact,
// so Cash is the only option offered in the selector.
const paymentMethodOptions = computed(() => [
  {
    label: t("cashtransfer.v2.method-cash"),
    value: "CASH",
    icon: markRaw(BanknoteIcon),
    bgClass: "bg-emerald-50",
    colorClass: "text-emerald-600",
  },
]);

const selectedPaymentMethodOption = computed(() => {
  return paymentMethodOptions.value.find(
    (m) => m.value === paymentMethod.value
  );
});

// ---------- Locations (real backend list, with name-based icons) ----------
type LocationOption = {
  value: number | string;
  label: string;
  icon: any;
  bgClass: string;
  colorClass: string;
  isCurrent?: boolean;
};

const hardwareLocId = computed(() => {
  return DeviceModule.hardwareLocation
    ? Number(DeviceModule.hardwareLocation.locationId)
    : null;
});

// Counterparties come from every cash location of the group — a location
// exists even when no device is attached to it (e.g. a safe), which a
// device-based list would miss. Canceled locations, kiosks and this
// device's own location stay excluded.
const locationOptions = computed<LocationOption[]>(() => {
  return (DeviceModule.locations || [])
    .filter(
      (l: any) =>
        !l.canceled &&
        Number(l.id) !== hardwareLocId.value &&
        l.deviceType !== CashLocationDeviceType.KIOSK
    )
    .map((l: any) => ({
      value: Number(l.id),
      label: String(l.name ?? ""),
      ...locationVisualFor(l.deviceType),
    }));
});

const selectedLocationOption = computed(() => {
  return locationOptions.value.find((o) => o.value === locationId.value);
});

// ---------- Review-step computeds ----------
const deviceLocationOption = computed<LocationOption>(() => {
  const name =
    DeviceModule.hardwareLocation?.deviceName ||
    t("cashtransfer.v2.location-pos-device");
  return {
    value: "device",
    label: `${name} (${t("cashtransfer.v2.current-device")})`,
    icon: markRaw(MonitorIcon),
    bgClass: "bg-[#044c55]/10",
    colorClass: "text-[#044c55]",
    isCurrent: true,
  };
});

// Visual layout for the review step: the counterparty location is always on
// top and the current device anchored at the bottom, regardless of direction.
// The arrow conveys in (↓ into the device) vs out (↑ out of the device).
const reviewTopParty = computed(() => selectedLocationOption.value);
const reviewBottomParty = computed(() => deviceLocationOption.value);
const reviewArrowIcon = computed(() =>
  transferType.value === "in" ? ArrowDownIcon : ArrowUpIcon
);

const selectedPaymentMethodLabel = computed(() => {
  return (
    paymentMethodOptions.value.find((m) => m.value === paymentMethod.value)
      ?.label || paymentMethod.value
  );
});

// Multi-currency helpers
function hasAmountFor(label: string): boolean {
  return Number(amounts.value[label] || 0) > 0;
}
const nonZeroEntries = computed(() => {
  return availableCurrencies.value
    .map((c) => ({
      currency: c.label,
      amount: Number(amounts.value[c.label] || 0),
    }))
    .filter((e) => e.amount > 0);
});
const multiCurrencyHint = computed(() => nonZeroEntries.value.length > 1);
function formatMoney(value: number): string {
  return value.toLocaleString("en-EN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Swiss-style grouping (1'234.50) for the current-drawer hint.
function formatDrawer(value: number): string {
  return value
    .toLocaleString("en-EN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .split(",")
    .join("'");
}

// Current drawer balance for the selected currency (stored in cents on the
// V2 cashfloat). Used as a reference when transferring cash out.
const currentDrawerAmount = computed<number>(() => {
  // Prefer live amounts from API (reflects all transfers); fall back to store.
  const liveCents = liveAmounts.value[currency.value];
  if (liveCents !== undefined) return liveCents / 100;
  const cf = CashFloatModule.cashFloat;
  const cents =
    cf?.amounts?.[currency.value as Currencies] ??
    cf?.startAmounts?.[currency.value as Currencies] ??
    0;
  return cents / 100;
});

const locationIdAsNumber = computed<number | null>(() => locationId.value);
const hardwareLocationId = hardwareLocId;

// ---------- Validation ----------
const canConfirm = computed(() => {
  if (!locationId.value) return false;
  if (!paymentMethod.value) return false;
  if (nonZeroEntries.value.length === 0) return false;
  return true;
});

const exceedsDrawer = computed(() => {
  if (transferType.value !== "out") return false;
  const entered = Number(amounts.value[currency.value] || 0);
  return entered > 0 && entered > currentDrawerAmount.value;
});

// ---------- Actions ----------
function onOpenChange(open: boolean) {
  if (!open) closeModal();
}

function closeModal() {
  ModalModule.setStatus(ModalStatus.Hidden);
}

async function submit() {
  if (!canConfirm.value) return;
  isLoading.value = true;
  const isCashOut = transferType.value === "out";
  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;

  // Multi-currency: one ICashTransfer per non-zero currency entry. All share
  // the same source/destination, payment method and note.
  for (const entry of nonZeroEntries.value) {
    const cashTransfer: ICashTransfer = {
      _id: dayjs().toISOString() + "-" + entry.currency,
      createdAt: dayjs().toISOString(),
      amount: entry.amount,
      currency: entry.currency as any,
      groupId: Number(groupId),
      from: isCashOut ? hardwareLocationId.value : locationIdAsNumber.value,
      to: isCashOut ? locationIdAsNumber.value : hardwareLocationId.value,
      note: note.value.trim(),
      channel: "Smeetz POS",
      uuid: randomstring.generate(10),
      cfUid: CashFloatModule.cashFloat?.uid ?? null,
    };

    const isSynced = await handleCashTransferSync(cashTransfer);
    if (!isSynced) {
      isLoading.value = false;
      return;
    }

    try {
      await processMoneyInOutEvent({
        moneyInOutType: isCashOut
          ? MoneyInOutType.PAY_OUT
          : MoneyInOutType.PAY_IN,
        amount: isCashOut ? -entry.amount : entry.amount,
        description: `Cash transfer ${isCashOut ? "OUT" : "IN"} (V2 ${
          entry.currency
        })`,
        posDateTime: dayjs().toISOString(),
        paymentType: PaymentMethod.CASH,
        cashLocation: mapCashLocation(locationIdAsNumber.value),
      });
    } catch (error) {
      LogService.info("V2 cash transfer: failed money in/out event", {
        error,
      });
    }

    const signed = isCashOut ? -1 * entry.amount : entry.amount;
    CashFloatModule.updateTransfersAmount(signed * 100);
    CashFloatModule.updateCashFloatAmount({
      currency: entry.currency,
      deltaCents: signed * 100,
    });
    const cur = entry.currency;
    liveAmounts.value[cur] =
      (liveAmounts.value[cur] ??
        CashFloatModule.cashFloat?.amounts?.[cur as Currencies] ??
        0) +
      signed * 100;
    if (isCashOut) {
      CashFloatModule.updateSumOfCashOut(entry.amount * 100);
    } else {
      CashFloatModule.updateSumOfCashIn(entry.amount * 100);
    }
    await signCashTransferTransaction(cashTransfer.uuid, signed);
  }

  NotificationService.showCustomSuccess(t("cashtransfer.cash-in-out-success"));

  isLoading.value = false;
  step.value = "success";

  // Force re-render of closing screen if open
  if (CashFloatModule.isClosingCashfloat) {
    CashFloatModule.setIsClosingCashfloat(false);
    setTimeout(() => CashFloatModule.setIsClosingCashfloat(true), 0);
  }
}

function startNewTransfer() {
  // Reset the form to begin another transfer in the same modal.
  step.value = "form";
  transferType.value = "in";
  locationId.value = null;
  amounts.value = {};
  note.value = "";
  paymentMethod.value = "";
  currency.value = defaultCurrency.value;
}
</script>

<!--
  Not scoped on purpose: SDialogContent is teleported to <body>, so a scoped
  :deep() can't reach its built-in close button. We target it via the
  `cash-in-out-v2` class that's forwarded onto the dialog content.
-->
<style lang="scss">
.cash-in-out-v2[data-slot="dialog-content"] [data-slot="dialog-close"] {
  top: 1.5rem;
  right: 1.5rem;
  opacity: 1;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
